import { Link } from "react-router-dom";
import { ClipboardList, Boxes, PackageOpen, Siren, Warehouse, Truck, ArrowRight, Phone, Home, Wrench, Building, FileCheck, HelpCircle } from "lucide-react";
import { SITE, IMG } from "@/lib/site";
import FAQ from "@/components/FAQ";
import Chapter from "@/components/cinema/Chapter";
import BeforeAfterSlider from "@/components/cinema/BeforeAfterSlider";
import { Reveal, LineReveal } from "@/components/cinema/Reveal";

const services = [
  {
    id: "contents", icon: ClipboardList, title: "Contents Assessment & Inventory Management",
    desc: "Detailed documentation, inventory preparation and contents evaluation services that support restoration projects, insurance claims and property recovery.",
    bullets: ["Room by room inventory and photographic documentation", "Categorization of salvageable, non-salvageable and high-value items", "Claims-ready reporting for adjusters and contractors"],
    image: IMG.contentsAssessment,
  },
  {
    id: "packout", icon: Boxes, title: "Pack-Out Services",
    desc: "Professional packing, labeling and transportation of contents to protect belongings during restoration, reconstruction and emergency mitigation projects.",
    bullets: ["Careful pack-out with quality materials and labeling", "Coordinated logistics with restoration contractors", "Chain-of-custody tracking from site to storage"],
    image: IMG.packout,
  },
  {
    id: "packback", icon: PackageOpen, title: "Pack-Back Services",
    desc: "Careful delivery, unpacking and placement of contents after repairs are completed, restoring comfort and organization to properties.",
    bullets: ["Unpacking and placement matched to original layout", "Reassembly and setup of furniture and fixtures", "Final walk-through with the client"],
    image: IMG.packback,
  },
  {
    id: "emergency", icon: Siren, title: "Emergency Restoration Support",
    desc: "Fast emergency response for water, fire, storm and property damage losses. We help prevent further damage and stabilize affected areas.",
    bullets: ["24/7 dispatch across Toronto and the GTA", "Initial stabilization and contents protection", "Coordination with restoration contractors and insurers"],
    image: IMG.emergencyOps,
  },
  {
    id: "storage", icon: Warehouse, title: "Storage Solutions",
    desc: "Secure storage for personal and commercial contents while restoration or renovation work is completed.",
    bullets: ["Secure, monitored storage environments", "Short-term and extended project storage", "Inventory tracking through the project lifecycle"],
    image: IMG.warehouseContents,
  },
  {
    id: "moving", icon: Truck, title: "Professional Moving Services",
    desc: "Coordinated residential and commercial moving services delivered with the same care and discipline our restoration work demands.",
    bullets: ["Residential, commercial and project-based moves", "Trained crews and professional equipment", "Coordinated scheduling around restoration timelines"],
    image: IMG.crewTruck,
  },
];

const audiences = [
  { icon: Home, t: "Homeowners", d: "Compassionate, transparent help when life is disrupted. We protect your belongings and walk you through every step." },
  { icon: Wrench, t: "Restoration Contractors", d: "A dependable contents partner that integrates cleanly with your mitigation, demolition and reconstruction schedules." },
  { icon: Building, t: "Property Managers", d: "Coordinated response across multi-unit buildings, tenants and ownership groups. Minimal disruption to operations." },
  { icon: FileCheck, t: "Insurance Professionals", d: "Documented, claims-ready inventory and contents handling that streamlines adjusting and reduces friction on the file." },
];

function ServiceChapter({ s, i }) {
  const flip = i % 2 === 1;
  return (
    <Chapter img={s.image} overlay={flip ? "right" : "left"} className="chapter-compact" testId={`service-detail-${s.id}`}>
      <div id={s.id} className="scroll-mt-28" />
      <div className={`grid md:grid-cols-12 gap-12 items-center`}>
        <div className={`md:col-span-6 ${flip ? "md:col-start-7" : ""}`}>
          <Reveal y={16}>
            <div className="flex items-center gap-4 mb-6">
              <span className="w-12 h-12 grid place-items-center bg-[hsl(var(--gaza-blue-bright))]"><s.icon className="w-6 h-6 text-white" strokeWidth={1.8} /></span>
              <span className="text-[11px] tracking-[0.28em] uppercase font-extrabold text-white/60">Service {String(i + 1).padStart(2, "0")}</span>
            </div>
          </Reveal>
          <LineReveal className="display-serif text-3xl md:text-5xl leading-[0.98]" lines={[s.title]} />
          <Reveal delay={0.3}><p className="text-white/85 text-lg mt-6 leading-relaxed">{s.desc}</p></Reveal>
          <Reveal delay={0.45}>
            <ul className="mt-7 space-y-3">
              {s.bullets.map((b) => (
                <li key={b} className="flex gap-3 text-white/85">
                  <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gaza-blue-bright))] mt-2.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </Chapter>
  );
}

export default function Services() {
  return (
    <div data-testid="page-services" className="bg-[hsl(var(--gaza-teal-deep))] text-white">
      <Chapter img={IMG.emergencyOps} overlay="left" testId="services-header">
        <div className="max-w-5xl">
          <Reveal y={16}><div className="eyebrow eyebrow-light mb-6">Our Services</div></Reveal>
          <LineReveal as="h1" className="display-serif text-4xl sm:text-5xl md:text-6xl leading-[0.95]" lines={["Comprehensive", "contents, restoration", "& moving services."]} />
          <Reveal delay={0.5}>
            <p className="text-white/80 text-lg mt-8 leading-relaxed max-w-2xl">
              Six tightly integrated service lines that move with you from first emergency call through final pack-back, across Toronto and the GTA.
            </p>
          </Reveal>
        </div>
      </Chapter>

      <Chapter img={IMG.stageDrying} overlay="dark" testId="services-before-after">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-6">Before / After</div></Reveal>
            <LineReveal className="display-serif text-4xl md:text-6xl leading-[0.95]" lines={["Same house.", "Same street.", <span className="text-[hsl(var(--gaza-blue-bright))]">Back home.</span>]} />
            <Reveal delay={0.45}>
              <p className="text-white/80 text-lg mt-8 leading-relaxed max-w-md">
                Drag the handle to see a flooded GTA home the night we were called, and the same property after extraction, drying, cleaning and pack-back.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="md:col-span-7">
            <BeforeAfterSlider before={IMG.stageFlood} after={IMG.stageBackHome} />
          </Reveal>
        </div>
      </Chapter>

      <div data-testid="services-detail">
        {services.map((s, i) => <ServiceChapter key={s.id} s={s} i={i} />)}
      </div>

      <Chapter img={IMG.contentsPackout} overlay="dark" testId="services-audiences">
        <div className="max-w-2xl mb-14">
          <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">Who we serve</div></Reveal>
          <LineReveal className="display-serif text-4xl md:text-5xl leading-[0.98]" lines={["Service lines tailored", "to the people on every", "restoration project."]} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {audiences.map((a, i) => (
            <Reveal key={a.t} delay={0.15 + i * 0.1} className="h-full">
              <div className="glass-dark h-full p-10" data-testid={`services-audience-${a.t.toLowerCase().replace(/\s+/g, "-")}`}>
                <a.icon className="w-7 h-7 text-[hsl(var(--gaza-blue-bright))]" strokeWidth={1.6} />
                <h3 className="display-serif text-2xl mt-5">{a.t}</h3>
                <p className="text-white/75 mt-3 leading-relaxed">{a.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Chapter>

      <Chapter img={IMG.torontoBlueHour} overlay="dark" testId="services-faq">
        <div className="text-center mb-16" data-testid="services-cta">
          <LineReveal className="display-chunky text-3xl md:text-5xl max-w-3xl mx-auto leading-[0.95]" lines={["Need rapid response", "or planning a project?"]} />
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={SITE.phoneHref} className="btn-gold" data-testid="services-call-btn"><Phone className="w-4 h-4" /> Call {SITE.phone}</a>
              <Link to="/contact" className="btn-outline" data-testid="services-contact-btn">Request a Consultation <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Reveal y={16}><HelpCircle className="w-8 h-8 text-[hsl(var(--gaza-blue-bright))]" strokeWidth={2} /></Reveal>
            <Reveal y={16} delay={0.1}><div className="eyebrow eyebrow-light mt-5 mb-4">Common questions</div></Reveal>
            <LineReveal delay={0.15} className="display-chunky text-4xl md:text-5xl leading-[0.92]" lines={["Answers,", "before you ask."]} />
            <Reveal delay={0.45}>
              <p className="text-white/80 mt-5 leading-relaxed">Still not sure? Call us directly. Coordinators are on the line 24/7.</p>
              <a href={SITE.phoneHref} className="mt-8 btn-gold" data-testid="faq-call-btn"><Phone className="w-4 h-4" /> Call now</a>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="md:col-span-8">
            <div className="glass-panel p-6 md:p-10"><FAQ /></div>
          </Reveal>
        </div>
      </Chapter>
    </div>
  );
}
